import React, { useState } from 'react';
import { DocumentNode } from 'graphql';

import AvailabilityScheduleEditView from 'coaching/components/CoachAvailabilityView/AvailabilityScheduleEditView';
import CoachAvailabilitySchedulesList, {
  ExactlyOneOrTwoSchedules,
} from 'coaching/components/CoachAvailabilityView/AvailabilitySchedulesList';
import CoachAvailabilitiesByDayList from 'coaching/components/CoachAvailabilityView/CoachAvailabilitiesByDayList';
import {
  CoachingAvailabilitySchedule,
  SingleDayCoachAvailability,
} from 'coaching/containers/CoachAvailabilityContainer';
import MediaObject from 'components/MediaObject';

type ViewMode = 'view' | 'edit';

export type Props = {
  schedules: CoachingAvailabilitySchedule[];
  coachTimezone: string;
  availabilities: SingleDayCoachAvailability[];
  weekOffset: number;
  weekStartDate: string;
  weekEndDate: string;
  onWeekForward: () => void;
  onWeekBack: () => void;
  availabilityQuery: { query: DocumentNode; variables?: Object };
};

export default function CoachAvailabilityView(props: Props) {
  const {
    schedules,
    coachTimezone,
    availabilities,
    weekOffset,
    weekStartDate,
    weekEndDate,
    onWeekForward,
    onWeekBack,
    availabilityQuery,
  } = props;

  // if a coach has no existing schedules, we force them into edit mode
  // so that they can create their first schedule
  const hasSchedules = schedules.length > 0;
  const defaultViewMode = hasSchedules ? 'view' : 'edit';
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [scheduleBeingEdited, setScheduleBeingEdited] = useState<
    CoachingAvailabilitySchedule | undefined
  >();

  function clearEditView() {
    setScheduleBeingEdited(undefined);
    setViewMode('view');
  }

  const firstTwoSchedules: ExactlyOneOrTwoSchedules = [schedules[0], schedules[1] ?? null];
  const mapViewModeToComponent: Record<ViewMode, React.ReactElement> = {
    view: (
      <MediaObject>
        <CoachAvailabilitySchedulesList
          schedules={firstTwoSchedules}
          onClickSchedule={schedule => {
            setScheduleBeingEdited(schedule);
            setViewMode('edit');
          }}
          onClickSetUpcomingSchedule={() => setViewMode('edit')}
          timezone={coachTimezone}
        />
        <CoachAvailabilitiesByDayList
          availabilities={availabilities}
          weekOffset={weekOffset}
          weekStartDate={weekStartDate}
          weekEndDate={weekEndDate}
          onWeekForward={onWeekForward}
          onWeekBack={onWeekBack}
          timezone={coachTimezone}
          availabilityQuery={availabilityQuery}
        />
      </MediaObject>
    ),
    edit: (
      <AvailabilityScheduleEditView
        scheduleBeingEdited={scheduleBeingEdited}
        initialTimezone={coachTimezone}
        onSubmit={clearEditView}
        onBack={hasSchedules ? clearEditView : undefined}
        availabilityQuery={availabilityQuery}
      />
    ),
  };
  return mapViewModeToComponent[viewMode];
}
